import { Link } from "@tanstack/react-router";
import { ArrowRight, HelpCircle, MessageCircle } from "lucide-react";

import { Section } from "@/components/layout/Page";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useI18n } from "@/context/I18nContext";
import { faqsForPage, type FaqPageKey } from "@/lib/mock/faqs";
import { cn } from "@/lib/utils";

/**
 * The FAQ block a content page publishes.
 *
 * Pair every use with faqHeadScript(page, path) in the route's head(): the
 * FAQPage markup has to describe the questions the page renders, and both read
 * the same faqsForPage() list so they cannot disagree.
 *
 * Answers are force-mounted. A collapsed Radix accordion keeps its content in
 * the DOM only with forceMount, and an answer a crawler cannot read is an
 * answer that does not count — the same reason the product FAQ does it.
 */
export function FaqSection({
  page,
  band = false,
  className,
  eyebrow,
  heading,
  lead,
}: {
  page: FaqPageKey;
  band?: boolean;
  className?: string;
  eyebrow?: string;
  heading?: string;
  lead?: string;
}) {
  const { t } = useI18n();
  const entries = faqsForPage(page);
  if (entries.length === 0) return null;

  // Locale overrides fall back to the English source, the convention product
  // and glossary copy already use.
  const tk = (key: string, fallback: string) => {
    const res = t(key);
    return res === key ? fallback : res;
  };

  return (
    <Section id="faq" band={band} className={cn("scroll-mt-24", className)}>
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <span className="mb-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-primary">
            <HelpCircle className="h-4 w-4" /> {eyebrow ?? t("faqSection.eyebrow")}
          </span>
          <h2 className="font-display text-2xl font-bold text-navy md:text-3xl">
            {heading ?? t("faqSection.heading")}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {lead ?? t("faqSection.lead")}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/faqs"
              className="inline-flex items-center text-sm font-bold text-primary hover:text-navy"
            >
              {t("faqSection.viewAll")} <ArrowRight className="ms-1.5 h-4 w-4" />
            </Link>
            <Link
              to="/contactus"
              className="inline-flex items-center text-sm font-bold text-navy hover:text-primary"
            >
              <MessageCircle className="me-1.5 h-4 w-4" /> {t("faqSection.ask")}
            </Link>
          </div>
        </div>

        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="w-full space-y-3">
            {entries.map((entry) => (
              <AccordionItem
                key={entry.slug}
                value={entry.slug}
                id={`faq-${entry.slug}`}
                className="glass-card scroll-mt-28 rounded-xl border border-border/80 px-5"
              >
                <AccordionTrigger className="py-4 text-start font-display text-sm font-semibold text-navy hover:no-underline">
                  {tk(`faq.${entry.slug}.q`, entry.q)}
                </AccordionTrigger>
                <AccordionContent
                  forceMount
                  className="pb-4 text-xs leading-relaxed text-muted-foreground"
                >
                  {tk(`faq.${entry.slug}.a`, entry.a)}
                  {entry.link && (
                    <Link
                      to={entry.link.to}
                      className="mt-2.5 inline-flex items-center font-bold text-primary hover:text-navy"
                    >
                      {tk(`faq.${entry.slug}.link`, entry.link.label)}
                      <ArrowRight className="ms-1 h-3 w-3" />
                    </Link>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </Section>
  );
}
